import { useCallback, useEffect, useState } from "react";
import { Check, Copy, ExternalLink } from "lucide-react";
import { AdminLayout } from "../../components/Layouts";
import { api, readUser, fmt, errMsg } from "../../lib/api";

export default function AdminWithdrawals() {
  const user = readUser();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ tx_hash: "", note: "" });
  const [notice, setNotice] = useState("");
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/admin/withdrawals", { params: filter ? { status: filter } : {} });
    setRows(r.data);
  }, [filter]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  function openModal(withdrawal, mode) {
    setCopied(false);
    setCopyError("");
    setForm({ tx_hash: "", note: "" });
    setModal({ withdrawal, mode });
  }

  function closeModal() {
    setModal(null);
    setCopied(false);
    setCopyError("");
    setForm({ tx_hash: "", note: "" });
  }

  async function copyWallet() {
    if (!modal?.withdrawal?.wallet_address) return;
    const wallet = modal.withdrawal.wallet_address;
    setCopyError("");
    let success = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(wallet);
        success = true;
      }
    } catch (_) {
      success = false;
    }
    if (!success) {
      const textarea = document.createElement("textarea");
      textarea.value = wallet;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try { success = document.execCommand("copy"); } catch (_) { success = false; }
      document.body.removeChild(textarea);
    }
    if (success) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } else {
      setCopyError("Adres otomatik kopyalanamadı. Adrese basılı tutarak kopyalayın.");
    }
  }

  async function act(w, action) {
    setNotice("");
    try {
      await api.post(`/admin/withdrawals/${w.id}/${action}`, action === "complete" ? form : {});
      closeModal(); load();
    } catch (e) { setNotice(errMsg(e)); }
  }

  const filters = ["", "Pending", "Processing", "Completed", "Rejected"];
  return (
    <AdminLayout user={user} active="Çekimler">
      <section className="admin-panel">
        <div className="admin-panel-title">
          <div><span className="eyebrow">İŞLEM KUYRUĞU</span><h2>Çekimler</h2></div>
          <div className="filter-pills">
            {filters.map((f) => <button key={f || "all"} className={filter === f ? "pill active" : "pill"} onClick={() => setFilter(f)} data-testid={`admin-wd-filter-${f || "all"}`}>{f || "Tümü"}</button>)}
          </div>
        </div>
        {notice && <div className="error" data-testid="admin-wd-notice">{notice}</div>}
        <div className="table-wrap">
          <table className="admin-withdrawals-table">
            <thead><tr><th>Kullanıcı</th><th>Tutar</th><th>Ücret</th><th>Net</th><th>Cüzdan</th><th>Tarih</th><th>Durum</th><th>TX Hash</th><th>İşlem</th></tr></thead>
            <tbody>
              {rows.map((w, i) => (
                <tr key={w.id} data-testid={`admin-wd-${i}`}>
                  <td data-label="Kullanıcı">{w.user_email}</td>
                  <td data-label="Tutar">${fmt(w.amount)}</td>
                  <td data-label="Ücret">${fmt(w.fee)}</td>
                  <td data-label="Net">${fmt(w.net_amount)}</td>
                  <td data-label="Cüzdan" className="hash" data-testid={`admin-wd-wallet-${i}`} title={w.wallet_address || ""}>{(w.wallet_address || "").slice(0, 14)}…</td>
                  <td data-label="Tarih">{new Date(w.created_at).toLocaleString("tr-TR")}</td>
                  <td data-label="Durum"><span className={`status ${w.status.toLowerCase()}`}>{w.status}</span></td>
                  <td data-label="TX Hash" className="hash">{(w.tx_hash || "").slice(0, 12) || "—"}</td>
                  <td data-label="İşlem"><div className="mobile-table-actions">
                    {w.status === "Pending" && <button className="table-action approve" data-testid={`admin-wd-process-${i}`} onClick={() => openModal(w, "process")}>İşleme Al</button>}
                    {(w.status === "Pending" || w.status === "Processing") && <button className="table-action approve" data-testid={`admin-wd-complete-${i}`} onClick={() => openModal(w, "complete")}>Tamamla</button>}
                    {(w.status === "Pending" || w.status === "Processing") && <button className="table-action reject" data-testid={`admin-wd-reject-${i}`} onClick={() => act(w, "reject")}>Reddet</button>}
                  </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <div className="table-empty">Çekim bulunmuyor.</div>}
        </div>
      </section>

      {modal && (
        <div className="modal-wrap" onClick={closeModal} data-testid="admin-wd-modal-overlay">
          <div className="modal withdrawal-review-modal" onClick={(e) => e.stopPropagation()} data-testid="admin-wd-modal">
            <h3 data-testid="admin-wd-modal-title">{modal.mode === "process" ? "Çekimi İşleme Al" : "Çekimi Tamamla"}</h3>
            <p className="note">Gönderim yapmadan önce alıcı adresini ve net tutarı dikkatle doğrulayın.</p>
            <div className="withdrawal-review-grid">
              <div><span>Kullanıcı</span><strong data-testid="admin-wd-modal-user">{modal.withdrawal.user_email}</strong></div>
              <div><span>Gönderilecek Net Tutar</span><strong data-testid="admin-wd-modal-net">${fmt(modal.withdrawal.net_amount)} USDT</strong></div>
            </div>
            <div className="withdrawal-wallet-review">
              <span>BEP20 Alıcı Cüzdanı</span>
              <code data-testid="admin-wd-modal-wallet">{modal.withdrawal.wallet_address || "Cüzdan adresi bulunamadı"}</code>
              <div className="withdrawal-wallet-actions">
                <button type="button" className="btn btn-ghost" onClick={copyWallet} disabled={!modal.withdrawal.wallet_address} data-testid="admin-wd-copy-wallet">
                  {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Kopyalandı" : "Adresi Kopyala"}
                </button>
                {modal.withdrawal.wallet_address && (
                  <a className="btn btn-ghost" href={`https://bscscan.com/address/${modal.withdrawal.wallet_address}`} target="_blank" rel="noreferrer" data-testid="admin-wd-bscscan-wallet">
                    <ExternalLink size={15} /> BscScan
                  </a>
                )}
              </div>
              {copyError && <div className="error" data-testid="admin-wd-copy-error">{copyError}</div>}
            </div>
            {modal.mode === "complete" && (<>
              <label>TX Hash<input data-testid="admin-wd-txhash" value={form.tx_hash} onChange={(e) => setForm({ ...form, tx_hash: e.target.value })} required /></label>
              <label>Not<input data-testid="admin-wd-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></label>
            </>)}
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={closeModal} data-testid="admin-wd-cancel">İptal</button>
              <button className="btn btn-accent" disabled={!modal.withdrawal.wallet_address} onClick={() => act(modal.withdrawal, modal.mode)} data-testid="admin-wd-save">
                {modal.mode === "process" ? "İşleme Al" : "Tamamla"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
